import { useCallback, useEffect, useState } from "react";
import { NOTIFICATION_STATUS_LABELS, type Notification } from "@shared/modules/x-notifications";
import { api } from "../../../core/lib/api";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Badge } from "../../../core/components/ui/badge";
import { formatDateTime } from "../../../core/lib/format";

interface Outbox {
  configured: boolean;
  notifyEmail: string | null;
  items: Notification[];
}

const VARIANT: Record<Notification["status"], "neutral" | "success" | "warning" | "danger"> = {
  en_attente: "neutral",
  envoyee: "success",
  simulee: "warning",
  echec: "danger",
};

export function OutboxPage() {
  const push = useToastStore((s) => s.push);
  const [data, setData] = useState<Outbox | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api<Outbox>("/api/notifications").then(setData).catch(() => push("danger", "Chargement impossible")), [push]);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="page">
      <PageHeader
        title="Notifications"
        subtitle="Courriels d'alerte envoyés par la plateforme."
        actions={
          <Button
            variant="primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api<{ to: string; simulated: boolean }>("/api/notifications/test", { method: "POST", body: "{}" });
                push(r.simulated ? "warning" : "success", r.simulated ? `Courriel simulé vers ${r.to} (Mailjet non configuré)` : `Courriel d'essai envoyé à ${r.to}`);
                await load();
              } finally {
                setBusy(false);
              }
            }}
          >
            Envoyer un courriel d'essai
          </Button>
        }
      />
      <Card title="Configuration">
        <dl className="kv">
          <dt>Mailjet</dt>
          <dd>{data?.configured ? <Badge variant="success">configuré</Badge> : <Badge variant="warning">non configuré : courriels simulés</Badge>}</dd>
          <dt>Adresse des alertes</dt>
          <dd>{data?.notifyEmail ?? <span className="muted">NOTIFY_EMAIL absent : aucune alerte interne</span>}</dd>
        </dl>
      </Card>
      <table className="table">
        <thead>
          <tr>
            <th>Quand</th>
            <th>Destinataire</th>
            <th>Objet</th>
            <th>Statut</th>
          </tr>
        </thead>
        <tbody>
          {(data?.items ?? []).map((n) => (
            <tr key={n.id}>
              <td>{formatDateTime(n.createdAt, { dateStyle: "short", timeStyle: "short" })}</td>
              <td>{n.toEmail}</td>
              <td>{n.subject}</td>
              <td>
                <Badge variant={VARIANT[n.status]}>{NOTIFICATION_STATUS_LABELS[n.status]}</Badge>
                {n.error ? <span className="muted"> · {n.error}</span> : null}
              </td>
            </tr>
          ))}
          {data && data.items.length === 0 ? (
            <tr>
              <td colSpan={4} className="table__empty">
                Aucun courriel.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
